import { z } from "zod";
import {
  CreateTaskDto,
  RecurrenceFrequencySchema,
  ScheduleTaskDto,
  Task,
  TaskStatus,
  TaskStatusSchema,
  TaskType,
  TaskTypeSchema,
  UpdateTaskDto,
} from "@myapp/contract";
import {
  formatDateForInput,
  fromDatetimeLocal,
  toDatetimeLocal,
} from "../../../shared/helpers/date";
import {
  overrideFields,
  type ScheduleOverrides,
  type ScheduleSlot,
} from "../../../shared/lib/schedule";

/**
 * One form for create and edit, and for both task types — which now means *one* form, not two
 * behind a switch.
 *
 * THE RULE THIS FILE ENCODES:
 *   Der Handwerker ist Pflicht. Sonst nichts.
 *   Termin und Abgabefrist sind beide optional und unabhängig voneinander: eine Aufgabe kann
 *   beides haben, eines von beidem, oder keines — dann bleibt sie ungeplant und wird später im
 *   Kalender geplant.
 *   Der Aufgabentyp entscheidet über **gar nichts** mehr. Er ist nur noch ein Etikett für Filter,
 *   Liste und Arbeitszeit-Report.
 *
 * WHY THE TYPE DROPPED OUT: it never had a meaning outside this file. `CreateTaskDtoSchema`
 * accepts `plannedStart` and `deadline` in any combination, `CreateTaskUseCase` never reads
 * `taskType` at all, and the API's own `deadlineForOccurrence` carries a deadline across a whole
 * series — i.e. the backend has always assumed one task can hold both. The list page has drawn a
 * Datum *and* an Abgabefrist column per row for just as long. The split lived here alone.
 *
 * WHAT STILL BINDS THE TERMIN TOGETHER: `CreateTaskUseCase` only reserves a slot when a technician
 * AND a start are both present — otherwise it saves an unscheduled task and **silently drops the
 * durations**. So a start without a work duration is worse than no start at all, and that pairing
 * is the one thing `superRefine` below still checks.
 *
 * THE OTHER TRAP: `plannedStart` is the **work** start and round-trips through
 * `schedule.plannedWorkStartsAt`, never `schedule.startsAt` — the latter is the drawn block and
 * begins `plannedTravelMinutes` earlier. Reading it back would walk every task backwards on save.
 *
 * THE THIRD TRAP, and what `toSlot` / `applySlot` exist for: occupancy is `COALESCE(actual, planned)`
 * per component, so a **completed** Aufgabe is drawn from its Ist-Zeiten. The four values the board
 * reads and writes therefore have to be the Ist ones for as long as the form says the task is done —
 * reading `plannedStart` there would make the block jump off the position it was drawn at the moment
 * the form opened, and writing it back would save a move that never becomes visible. And in reverse:
 * actuals sent for a task that is *not* completed would resize its block with a 200 and no error
 * anywhere. `taskHasActuals` decides the side in exactly one place, here.
 */

/**
 * Actuals are collected once the Handwerker is done, so the form offers them for the one completed
 * state. The task twin of `hasActuals` in `features/appointment-upsert` — single source of truth for
 * both the fields and the update mapper, which must not drift apart.
 */
export function taskHasActuals(status?: TaskStatus): boolean {
  return status === "COMPLETED";
}

const recurrenceFormSchema = z.object({
  frequency: RecurrenceFrequencySchema,
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format TT.MM.JJJJ"),
});

/**
 * German messages on every constraint, including the ones that are hard to reach.
 *
 * Zod's defaults are English ("Number must be greater than or equal to 0"), and this UI is not.
 * The duration fields rarely surface one — `FormInput` maps an empty box to `""` and `z.coerce`
 * turns that into `0` — but a pasted `-30` or `1.5` reaches them, and an untranslated sentence in
 * the middle of a German form is worse than the field it belongs to.
 */
const MINUTES = { invalid: "Ganze Minuten angeben", negative: "Darf nicht negativ sein" } as const;

export const taskFormSchema = z
  .object({
    title: z.string().min(1, "Pflichtfeld"),
    description: z.string(),
    taskType: TaskTypeSchema,
    /** Edit only — a new task is always PLANNED. */
    status: TaskStatusSchema,
    /** Stays nullable so the empty form has a valid default; emptiness is caught below. */
    assignedTechnicianId: z.number().int().positive("Pflichtfeld").nullable(),
    /** `<input type="datetime-local">` value — local wall clock, no timezone. */
    plannedStart: z.string(),
    /** `<input type="date">` value. Optional, and open to both types. */
    deadline: z.string(),
    plannedWorkDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    plannedTravelDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    plannedDepartureDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    /**
     * Ist-Zeiten. Always present in the form state so the inputs stay controlled, but only surfaced
     * and only submitted for a completed task — see `taskHasActuals`.
     *
     * `actualStart` is the Ist **work** start, the twin of `plannedStart` and subject to the same
     * trap: it round-trips through `schedule.actualWorkStartsAt`, never through `startsAt`.
     */
    actualStart: z.string(),
    actualWorkDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    actualTravelDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    actualDepartureDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    attachments: z.array(z.string()),
    /** `null` === einmalig. Only offered on create; the frequency is not patchable. */
    recurrence: recurrenceFormSchema.nullable(),
    /** Edit only, and only meaningful on a task that has a `seriesId`. */
    forFullSeries: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });

    // Der einzige Pflichtwert. Eine Aufgabe ohne Termin ist gültig, eine ohne Handwerker nicht.
    if (data.assignedTechnicianId == null) issue("assignedTechnicianId", "Pflichtfeld");

    // Kein Termin: nichts weiter zu prüfen. Die Aufgabe wird ungeplant gespeichert und kann
    // später im Kalender geplant werden. Die Abgabefrist ist ebenfalls freiwillig.
    if (!data.plannedStart) {
      // Ausnahme: eine Serie ist eine Menge von Reservierungen und braucht daher einen Anker.
      // `RecurrenceField` sperrt sich ohne `startDate` zwar selbst, aber eine Regel, die *vor*
      // dem Leeren des Starts gesetzt wurde, überlebt das — und der Contract verlangt, dass eine
      // `seriesId` immer einen Termin hat.
      if (data.recurrence) issue("plannedStart", "Für eine Serie ist ein Termin nötig");
      return;
    }

    if (!fromDatetimeLocal(data.plannedStart)) {
      issue("plannedStart", "Ungültiges Datum");
    }

    // Half a termin is worse than none: the API would accept it, save the task unscheduled and
    // throw the times away without an error. Only reachable once a start is actually set.
    if (data.plannedWorkDuration <= 0) issue("plannedWorkDuration", "Pflichtfeld");
  });

export type TaskFormValues = z.infer<typeof taskFormSchema>;

export function getDefaultValues(taskType: TaskType = "GENERAL_TASK"): TaskFormValues {
  return {
    title: "",
    description: "",
    taskType,
    status: "PLANNED",
    assignedTechnicianId: null,
    plannedStart: "",
    deadline: "",
    plannedWorkDuration: 60,
    plannedTravelDuration: 0,
    plannedDepartureDuration: 0,
    actualStart: "",
    actualWorkDuration: 0,
    actualTravelDuration: 0,
    actualDepartureDuration: 0,
    attachments: [],
    recurrence: null,
    forFullSeries: false,
  };
}

/** `YYYY-MM-DD` from a date input → ISO, anchored at local midnight so it survives the round trip. */
function fromDateInput(value: string): string | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d)).toISOString();
}

/**
 * True when the form describes a real reservation — the single question every mapper below asks,
 * and the only thing that decides whether a task is scheduled. Both halves are required because
 * the backend only creates a `ScheduleEntry` when it has a technician *and* a start.
 *
 * There is deliberately no `ownsTermin` beside it any more: the task type used to veto a termin
 * before this function ever ran, which is exactly the rule that went away.
 */
export function isScheduled(values: TaskFormValues): boolean {
  return (
    values.assignedTechnicianId != null && fromDatetimeLocal(values.plannedStart) != null
  );
}

/** Everything `toSlot` and `applySlot` read. `status` decides which side of the COALESCE they use. */
export type SlotValues = Pick<
  TaskFormValues,
  | "assignedTechnicianId"
  | "plannedStart"
  | "plannedTravelDuration"
  | "plannedWorkDuration"
  | "plannedDepartureDuration"
  | "actualStart"
  | "actualTravelDuration"
  | "actualWorkDuration"
  | "actualDepartureDuration"
> & { status?: TaskStatus };

/**
 * The termin as a `ScheduleSlot` — **what the calendar draws** while the form is open, which for a
 * completed Aufgabe is its Ist-Zeiten and otherwise its Plan-Zeiten.
 *
 * The returned keys are named after the planned fields because they are the RHF field names the
 * board never inspects; see the note on `ScheduleSlot` itself. What they carry is the side
 * `taskHasActuals` selects.
 *
 * `null` means "there is no block to draw", which for a task is an ordinary state and not an error:
 * any task may sit unplanned, without a Handwerker or without a start. It is also `null` for a
 * duration input the user has just emptied (`valueAsNumber` hands back `NaN`) — the board then
 * keeps the last complete position rather than drawing the block at 06:00.
 *
 * The task type is not consulted and is not even in the `Pick`: every type can be drawn.
 *
 * The start stays the datetime-local **work** start, exactly as the board's `slotToDraft` expects
 * it. Mirrors `features/appointment-upsert`.
 */
export function toSlot(values: SlotValues): ScheduleSlot | null {
  const actuals = taskHasActuals(values.status);

  const start = actuals ? values.actualStart : values.plannedStart;
  const travel = actuals ? values.actualTravelDuration : values.plannedTravelDuration;
  const work = actuals ? values.actualWorkDuration : values.plannedWorkDuration;
  const departure = actuals
    ? values.actualDepartureDuration
    : values.plannedDepartureDuration;

  if (values.assignedTechnicianId == null) return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(start)) return null;
  if (![travel, work, departure].every(Number.isFinite)) return null;

  return {
    assignedTechnicianId: values.assignedTechnicianId,
    plannedStart: start,
    plannedTravelDuration: travel,
    plannedWorkDuration: work,
    plannedDepartureDuration: departure,
  };
}

/**
 * The inverse of `toSlot`: a slot chosen elsewhere — the board — written onto whichever side this
 * form's status puts it on.
 *
 * Used by the seeding `reset`, where the picked slot has to win over the loaded task. A plain spread
 * of the slot cannot do it any more: its keys are the *planned* field names, so on a completed
 * Aufgabe it would fill the Plan-Zeiten with a position the block does not use and leave the
 * Ist-Zeiten — the ones actually drawn — untouched. Mirrors `features/appointment-upsert`.
 */
export function applySlot(values: TaskFormValues, slot?: ScheduleSlot): TaskFormValues {
  if (!slot) return values;

  const scheduling = taskHasActuals(values.status)
    ? {
        actualStart: slot.plannedStart,
        actualTravelDuration: slot.plannedTravelDuration,
        actualWorkDuration: slot.plannedWorkDuration,
        actualDepartureDuration: slot.plannedDepartureDuration,
      }
    : {
        plannedStart: slot.plannedStart,
        plannedTravelDuration: slot.plannedTravelDuration,
        plannedWorkDuration: slot.plannedWorkDuration,
        plannedDepartureDuration: slot.plannedDepartureDuration,
      };

  return {
    ...values,
    assignedTechnicianId: slot.assignedTechnicianId,
    ...scheduling,
  };
}

/** Open to every task now, and independent of the termin — a task may be bounded by both. */
function deadlineIso(values: TaskFormValues): string | null {
  return values.deadline ? fromDateInput(values.deadline) : null;
}

export function toCreateDto(
  values: TaskFormValues,
  overrides: ScheduleOverrides = {},
): CreateTaskDto {
  const scheduled = isScheduled(values);

  return {
    title: values.title,
    description: values.description || undefined,
    taskType: values.taskType,
    assignedTechnicianId: values.assignedTechnicianId ?? null,
    plannedStart: scheduled ? fromDatetimeLocal(values.plannedStart) : null,
    deadline: deadlineIso(values) ?? undefined,
    // The three are required by the DTO even for a task that is never reserved; zeroes say
    // "no block" rather than leaving a duration the API would ignore anyway.
    plannedWorkDuration: scheduled ? values.plannedWorkDuration : 0,
    plannedTravelDuration: scheduled ? values.plannedTravelDuration : 0,
    plannedDepartureDuration: scheduled ? values.plannedDepartureDuration : 0,
    attachments: values.attachments.length ? values.attachments : undefined,
    // A series is a set of reservations, so it only exists where a termin does — which is now a
    // question about the termin alone, guarded in `superRefine` rather than by the type.
    recurrence: values.recurrence ?? undefined,
    ...overrideFields(overrides),
  };
}

/**
 * `recurrence` is deliberately absent: `UpdateTaskDtoSchema` does not accept it — changing a
 * frequency would mean re-reserving every future occurrence, which goes through
 * `PATCH /tasks/series/:seriesId` instead.
 *
 * The schedule fields are only sent when the task ALREADY owns an entry. `UpdateTaskUseCase`
 * silently ignores a schedule-shaped patch on an unscheduled task, so adding a termin to one
 * goes through `toScheduleDto` and `PATCH /tasks/:id/schedule`.
 *
 * The overrides are set only after the user confirmed the matching 409 dialog; without a schedule
 * patch they are inert, because the API only runs the checks they waive when the timing changes.
 *
 * The four `actual*` keys ride along only for a completed task, and only where the planned ones go —
 * they describe the same reservation. `actualStart` is what *anchors* the durations: a completed
 * Aufgabe is drawn from `actualWorkStartsAt ?? plannedWorkStartsAt`, so a move that wrote only the
 * durations would resize the block without relocating it. It is dropped rather than sent as `null`
 * for an unparseable value — `UpdateTaskDtoSchema` types it `z.string().datetime().nullable()`, and
 * a null there would *clear* an Ist start rather than leave it alone.
 *
 * The planned keys keep being sent unchanged on a completed task, deliberately: they carry what was
 * planned, the drag never touched them, and re-sending the loaded values preserves the comparison.
 */
export function toUpdateDto(
  values: TaskFormValues,
  options: ScheduleOverrides & { hasSeries: boolean; hasSchedule: boolean },
): UpdateTaskDto {
  const scheduled = isScheduled(values);

  return {
    title: values.title,
    description: values.description || null,
    taskType: values.taskType,
    status: values.status,
    // The DTO has no `null` here: unassigning is not a PATCH, it is a delete of the reservation.
    assignedTechnicianId: values.assignedTechnicianId ?? undefined,
    deadline: deadlineIso(values),
    attachments: values.attachments,
    ...(options.hasSchedule && scheduled
      ? {
          plannedStart: fromDatetimeLocal(values.plannedStart) as string,
          plannedWorkDuration: values.plannedWorkDuration,
          plannedTravelDuration: values.plannedTravelDuration,
          plannedDepartureDuration: values.plannedDepartureDuration,
          ...(taskHasActuals(values.status)
            ? {
                ...(fromDatetimeLocal(values.actualStart)
                  ? { actualStart: fromDatetimeLocal(values.actualStart) as string }
                  : {}),
                actualWorkDuration: values.actualWorkDuration,
                actualTravelDuration: values.actualTravelDuration,
                actualDepartureDuration: values.actualDepartureDuration,
              }
            : {}),
        }
      : {}),
    ...(options.hasSeries ? { forFullSeries: values.forFullSeries } : {}),
    ...overrideFields(options),
  };
}

/**
 * The payload for `PATCH /tasks/:id/schedule` — create-or-replace of the reservation. Returns
 * null when the form has no complete termin, which is a valid state for any task.
 */
export function toScheduleDto(
  values: TaskFormValues,
  overrides: ScheduleOverrides = {},
): ScheduleTaskDto | null {
  if (!isScheduled(values)) return null;

  return {
    plannedStart: fromDatetimeLocal(values.plannedStart) as string,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    ...overrideFields(overrides),
  };
}

export function fromTask(task: Task): TaskFormValues {
  return {
    title: task.title,
    description: task.description ?? "",
    taskType: task.taskType,
    status: task.status,
    assignedTechnicianId: task.assignedTechnicianId,
    // plannedWorkStartsAt, NOT startsAt — see the note at the top of this file.
    plannedStart: toDatetimeLocal(task.schedule?.plannedWorkStartsAt),
    deadline: formatDateForInput(task.deadline),
    plannedWorkDuration: task.schedule?.plannedWorkMinutes ?? 60,
    plannedTravelDuration: task.schedule?.plannedTravelMinutes ?? 0,
    plannedDepartureDuration: task.schedule?.plannedDepartureMinutes ?? 0,
    // Actuals are nullable until the Handwerker reports them; the planned value is the sensible
    // starting point for a manager correcting them, and never a silent 0. `actualStart` follows the
    // same fallback: a completed Aufgabe that was never *started* has no Ist start, and opening the
    // form on a blank one would publish nothing to the calendar at all.
    actualStart: toDatetimeLocal(
      task.schedule?.actualWorkStartsAt ?? task.schedule?.plannedWorkStartsAt,
    ),
    actualWorkDuration:
      task.schedule?.actualWorkMinutes ?? task.schedule?.plannedWorkMinutes ?? 60,
    actualTravelDuration:
      task.schedule?.actualTravelMinutes ?? task.schedule?.plannedTravelMinutes ?? 0,
    actualDepartureDuration:
      task.schedule?.actualDepartureMinutes ?? task.schedule?.plannedDepartureMinutes ?? 0,
    attachments: task.attachments ?? [],
    recurrence: null,
    forFullSeries: false,
  };
}
